// React
import React, { useEffect, useState } from 'react';
// Translation
import i18n from 'i18next';
// React Bootstrap
import { Accordion, Button, Form, InputGroup } from 'react-bootstrap';
// Font Awesome
import { faCircleInfo, faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// Components
import { FhirStatus, StatusAlert, StatusIcon, Title } from '@fyrstain/fhir-front-library';
import AddTestScriptAssert from '../AddTestScriptAssert/AddTestScriptAssert';
import { Fixture } from '../FixturesSection/FixturesSection';
import { Variable } from '../VariablesSection/VariablesSection';

/////////////////////////////
//          Props          //
/////////////////////////////

interface OperationsSectionProps {
    // Choose the test step between 'setup', 'test', 'teardown'
    testStep: string;
    operations: any;
    asserts: any;
    // Set the operation form data to the parent component
    setOperationFormData: React.Dispatch<React.SetStateAction<any>>;
    // Retrieve the fixtures from the parent component to populate the sourceId select options
    fixtures: Fixture[],
    // Retrieve the origins from the parent component to populate the origin select options
    origins: { profile: string }[];
    // Retrieve the destinations from the parent component to populate the destination select options
    destinations: { profile: string }[];
    // Retrieve the variables from the parent component to populate the responseId select options
    variables: Variable[];
    // Set the formData to the parent component
    setAssertFormData?: React.Dispatch<React.SetStateAction<any>>;
    // Retrieve the validate profile id from the parent component to populate the responseId select options
    selectedProfiles?: { internalId: string, name: string }[];
}

const OperationsSection: React.FC<OperationsSectionProps> = (configs) => {

    /////////////////////////////
    //          Options        //
    /////////////////////////////

    // Options for the type of operation
    const operationTypeOptions = [
        { code: 'rest-operation', display: i18n.t('label.restoperation'), label: '' },
        { code: 'free-operation', display: i18n.t('label.freeoperation'), label: 'FreeOperation' },
    ];

    // Options for the type of REST operation
    const typeOptions = [
        { code: 'search', display: 'Search', label: 'SearchOperation' },
        { code: 'create', display: 'Create', label: 'CreateOperation' },
        { code: 'update', display: 'Update', label: 'UpdateOperation' },
        { code: 'read', display: 'Read', label: 'ReadOperation' },
        { code: 'vread', display: 'Vread', label: 'VreadOperation' },
        { code: 'delete', display: 'Delete', label: 'DeleteOperation' },
        { code: 'history', display: 'History', label: 'HistoryOperation' },
        { code: 'capabilities', display: 'Capabilities', label: 'CapabilitiesOperation' },
        { code: 'transaction', display: 'Transaction', label: 'BatchTransactionOperation' },
        { code: 'patch', display: 'Patch', label: 'PatchOperation' },
        { code: 'custom-operation', display: i18n.t('label.customoperation'), label: 'CustomOperation' },
    ];

    // Options for the method of operation
    const customMethodOptions = [
        { code: 'get', display: 'GET' },
        { code: 'post', display: 'POST' }, ,
    ];

    // Options for the targetId select options
    const combinedTargetIdOptions = [
        ...configs.fixtures.map(fixture => ({ id: fixture.internalId, name: fixture.id })),
        ...configs.variables.map(variable => ({ id: variable.internalId, name: variable.name }))
    ];

    /////////////////////////////
    //          States         //
    /////////////////////////////

    // Check if the edition form is validated before adding the operation
    const [editionValidated, setEditionValidated] = useState(false);

    // The form data to add an operation
    const [formData, setFormData] = useState({
        id: '' as string,
        description: '' as string,
        operationType: '' as string,
        type: {
            system: '' as string,
            code: '' as string
        },
        customSystem: '' as string,
        customCode: '' as string,
        method: '' as string,
        label: '' as string,
        resource: '' as string,
        returnResourceType: '' as string,
        accept: 'application/fhir+json' as string,
        contentType: '' as string,
        origin: '' as string,
        destination: '' as string,
        sourceId: '' as string,
        targetId: '' as string,
        responseId: '' as string,
        url: '' as string,
        params: '' as string,
        encodeRequestUrl: false as boolean,
        isCustomOperation: false as boolean
    });

    // The visibility of the test section
    const [displayTestSection, setDisplayTestSection] = useState(false);

    // The visibility of the form to add an operation after adding the first operation
    // TODO see if it's possible to use : (configs.operations && configs.operations.length === 0);
    const [showOperationForm, setShowOperationForm] = useState(false);

    // Messages for the required fields
    const [messages, setMessages] = useState<{ [key: string]: string }>({});

    // TODO see if you need a select with a manual entry for the targetId field
    // Manual input for the targetId field if we don't want to choose a fixture or variable added in this page
    const [manualInput, setManualInput] = useState('');
    const [isManualInput, setIsManualInput] = useState(false);

    // The operation type selected (REST operation or free operation) 
    const [operationType, setOperationType] = useState('');

    // Store the original id while editing 
    const [currentOperationId, setCurrentOperationId] = useState<string | null>(null);

    /////////////////////////////
    //          Constants      //
    /////////////////////////////

    // The fields for free or rest operations
    const operationTypeFields = {
        'free-operation': ['origin', 'destination', 'url', 'encodeRequestUrl'],
    };

    // The fields for each type of rest or custom operation
    const typeFields = {
        'search': [
            'accept',
            'contentType',
            'resource',
            'origin',
            'destination',
            'targetId',
            'responseId',
            'params',
            'encodeRequestUrl'
        ],
        'create': [
            'accept',
            'contentType',
            'resource',
            'origin',
            'destination',
            'sourceId',
            'responseId',
            'encodeRequestUrl'
        ],
        'update': [
            'accept',
            'contentType',
            'origin',
            'destination',
            'resource',
            'sourceId',
            'targetId',
            'responseId',
            'params',
            'encodeRequestUrl'
        ],
        'read': [
            'accept',
            'contentType',
            'origin',
            'destination',
            'resource',
            'targetId',
            'params',
            'responseId',
            'encodeRequestUrl'
        ],
        'vread': [
            'accept',
            'contentType',
            'origin',
            'destination',
            'resource',
            'targetId',
            'params',
            'responseId',
            'encodeRequestUrl'
        ],
        'delete': [
            'accept',
            'contentType',
            'origin',
            'destination',
            'resource',
            'targetId',
            'params',
            'responseId',
            'encodeRequestUrl'
        ],
        'history': [
            'accept',
            'contentType',
            'origin',
            'destination',
            'resource',
            'targetId',
            'params',
            'responseId',
            'encodeRequestUrl'
        ],
        'capabilities': [
            'accept',
            'contentType',
            'origin',
            'destination',
            'responseId',
            'encodeRequestUrl'
        ],
        'transaction': [
            'accept',
            'contentType',
            'origin',
            'destination',
            'sourceId',
            'responseId',
            'encodeRequestUrl'
        ],
        'custom-operation': [
            'customSystem',
            'customCode',
            'method',
            'resource',
            'returnResourceType',
            'accept',
            'contentType',
            'origin',
            'destination',
            'sourceId',
            'targetId',
            'responseId',
            'params',
            'encodeRequestUrl'
        ],
        'patch': [
            'accept',
            'contentType',
            'origin',
            'destination',
            'resource',
            'sourceId',
            'targetId',
            'responseId',
            'encodeRequestUrl'
        ],
    };

    // The required fields for each type of REST operation
    const requiredFieldsByType = {
        default: ['description', 'operationType', 'type'],
        create: ['resource', 'sourceId'],
        update: ['resource', 'sourceId'],
        read: ['targetId'],
        vread: ['targetId'],
        delete: ['resource'],
        transaction: ['sourceId'],
        patch: ['targetId', 'sourceId'],
        'custom-operation': ['customSystem', 'customCode', 'method'],
    };

    // The required fields for free operation
    const FreeOperationRequiredFields = {
        'free-operation': ['description', 'url'],
    };

    /////////////////////////////
    //          Tooltips       //
    /////////////////////////////

    // Translation for the sourceId information icon
    const sourceIdTooltip = i18n.t('tooltip.sourceid');

    // Translation for the targetId information icon
    const targetIdTooltip = i18n.t('tooltip.targetid');

    // Translation for the encode request url information icon
    const encodeRequestUrlTooltip = i18n.t('tooltip.encoderequesturl');

    // Translation for the params information icon
    const paramsTooltip = i18n.t('tooltip.params');

    // Translation for the resource information icon
    const resourceTooltip = i18n.t('tooltip.resource');

    // Translation for the accept information icon
    const acceptTooltip = i18n.t('tooltip.accept');

    // Translation for the content type information icon
    const contentTypeTooltip = i18n.t('tooltip.contenttype');

    // Translation for the responseId information icon
    const responseIdTooltip = i18n.t('tooltip.responseid');

    /////////////////////////////
    //        Functions        //
    /////////////////////////////

    /**
     * Generate a new operation id based on the test step
     * 
     * @param testStep 
     * @param index
     * 
     */
    const generateOperationId = (testStep: string, index: number): string => {
        switch (testStep) {
            case 'setup':
                return `SET-${String(index).padStart(2, '0')}`;
            case 'teardown':
                return `TED-${String(index).padStart(2, '0')}`;
            case 'test':
                return `TES-${String(index).padStart(2, '0')}`;
            default:
                throw new Error(`Invalid testStep: ${testStep}`);
        }
    };

    /**
     * The method based on the type of operation
     * 
     */
    const methodByTypeCode = (typeCode: string) => {
        let method = '';
        switch (typeCode) {
            case 'read':
            case 'vread':
            case 'history':
            case 'search':
            case 'capabilities':
                method = 'get';
                break;
            case 'update':
                method = 'put';
                break;
            case 'patch':
                method = 'patch';
                break;
            case 'delete':
                method = 'delete';
                break;
            case 'create':
            case 'transaction':
                method = 'post';
                break;
            case 'custom-operation':
                method = '';
                break;
            default:
                method = '';
                break;
        }
        return method;
    };

    /**
     * Check if the custom code or the custom system is valid 
     * 
     * @param customCode 
     * @param customSystem
     * 
     */
    const isValidCustomCodeAndSystem = ({ customCode, customSystem }: { customCode: string; customSystem: string }): boolean => {
        const pattern = /^[^\s]+$/;
        return pattern.test(customCode) && pattern.test(customSystem);
    };

    /**
     * Handle the change of the custom code and the custom system input field
     * 
     * @param name
     * @param value 
     * @param operation 
     * 
     */
    const handleCustomCodeAndSystemChange = (name: string, value: string, operation: any) => {
        handleChange({ target: { name, value } } as React.ChangeEvent<HTMLInputElement>, operation);
        if (name === 'customCode' && value && !/^[^\s]+$/.test(value)) {
            setMessages(prevMessages => ({
                ...prevMessages,
                customCode: i18n.t('errormessage.nowhitespace'),
            }));
        } else if (name === 'customCode') {
            setMessages(prevMessages => {
                const newMessages = { ...prevMessages };
                delete newMessages.customCode;
                return newMessages;
            });
        }
        if (name === 'customSystem' && value && !/^[^\s]+$/.test(value)) {
            setMessages(prevMessages => ({
                ...prevMessages,
                customSystem: i18n.t('errormessage.nowhitespace'),
            }));
        } else if (name === 'customSystem') {
            setMessages(prevMessages => {
                const newMessages = { ...prevMessages };
                delete newMessages.customSystem;
                return newMessages;
            });
        }
    };

    /**
     * Handle the change of the input fields
     * 
     * @param event
     * 
     */
    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>, operation?: any) => {
        const { name, value } = event.target;
        if (name === 'operationType') {
            const label = operationTypeOptions.find(option => option.code === 'free-operation')?.label || '';
            setFormData(prevFormData => ({
                ...prevFormData,
                operationType: value,
                label: label,
                type: {
                    system: '',
                    code: ''
                },
                customSystem: '',
                customCode: '',
                method: '',
                resource: '',
                returnResourceType: '',
                accept: 'application/fhir+json',
                contentType: '',
                origin: '',
                destination: '',
                sourceId: '',
                targetId: '',
                responseId: '',
                url: '',
                params: '',
                encodeRequestUrl: false as boolean
            }));
            setOperationType(value);
            setMessages({});
        } else if (name === 'type') {
            const method = methodByTypeCode(value);
            const label = typeOptions.find(option => option.code === value)?.label || '';
            setFormData(prevFormData => {
                const updatedFormData = {
                    ...prevFormData,
                    type: {
                        system: value === 'custom-operation' ? prevFormData.customSystem : "http://hl7.org/fhir/restful-interaction",
                        code: value
                    },
                    method: value === 'custom-operation' ? prevFormData.method : method,
                    label: label,
                    resource: '',
                    returnResourceType: '',
                    accept: 'application/fhir+json',
                    contentType: '',
                    origin: '',
                    destination: '',
                    sourceId: '',
                    targetId: '',
                    responseId: '',
                    url: '',
                    params: '',
                    encodeRequestUrl: false,
                    isCustomOperation: value === 'custom-operation'
                };
                return updatedFormData;
            });
            setMessages({});
        } else if (name === 'origin') {
            const selectedIndex = (configs.origins.findIndex(origin => origin.profile === value) + 1).toString();
            setFormData(prevFormData => ({
                ...prevFormData,
                [name]: selectedIndex
            }));
        } else if (name === 'destination') {
            const selectedIndex = (configs.destinations.findIndex(destination => destination.profile === value) + 1).toString();
            setFormData(prevFormData => ({
                ...prevFormData,
                [name]: selectedIndex
            }));
        } else if (name === 'sourceId' && value === '') {
            // Reset the targetId, url and params when the sourceId is empty to adapt to constraints
            setFormData(prevFormData => ({
                ...prevFormData,
                sourceId: value,
                targetId: '',
                url: '',
                params: ''
            }));
        } else {
            setFormData(prevFormData => ({
                ...prevFormData,
                [name]: value,
            }));
            // Delete the error message when the field is filled
            if (value) {
                setMessages(prevMessages => {
                    const newMessages = { ...prevMessages };
                    delete newMessages[name];
                    return newMessages;
                });
            }
            if (['sourceId', 'targetId', 'params', 'url'].includes(name) && value) {
                setMessages(prevMessages => {
                    const newMessages = { ...prevMessages };
                    delete newMessages.sourceId;
                    delete newMessages.targetId;
                    delete newMessages.params;
                    delete newMessages.url;
                    return newMessages;
                });
            }
        }
    };

    /**
     * Handle the form to edit the fixture from the edit icon
     * 
     * @param event 
     * 
     */
    const handleOperationEditingClick = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, operation: any) => {
        const setFormDataAndShowForm = () => {
            setCurrentOperationId(operation.id);
            setFormData({
                id: operation.id,
                description: operation.description,
                operationType: operation.operationType || formData.operationType,
                type: operation.type,
                customSystem: operation.type.system,
                customCode: operation.type.code,
                method: operation.method,
                label: operation.label,
                resource: operation.resource,
                returnResourceType: operation.returnResourceType,
                accept: operation.accept,
                contentType: operation.contentType,
                origin: operation.origin,
                destination: operation.destination,
                sourceId: operation.sourceId,
                targetId: operation.targetId,
                responseId: operation.responseId,
                url: operation.url,
                params: operation.params,
                encodeRequestUrl: operation.encodeRequestUrl,
                isCustomOperation: operation.isCustomOperation
            });
            setShowOperationForm(true);
        };
        if (currentOperationId && currentOperationId !== operation.id) {
            if (window.confirm(i18n.t('confirmationmessage.canceloperation'))) {
                setFormDataAndShowForm();
            }
        } else {
            setFormDataAndShowForm();
        }
    };

    /**
     * Handle the change of the select input field
     * 
     * @param event
     * @param operation 
     */
    const handleSelectChange = (event: any, operation: any) => {
        const value = event.target.value;
        if (value === 'manual') {
            setIsManualInput(true);
            handleChange({ target: { name: 'targetId', value: '' } } as React.ChangeEvent<HTMLInputElement>, operation);
        } else {
            setIsManualInput(false);
            handleChange(event, operation);
        }
    };

    /**
     * Handle the change of the manual input field
     * 
     * @param event 
     * @param operation 
     */
    // const handleManualInputChange = (event: any, operation: any) => {
    //     const value = event.target.value;
    //     setManualInput(value);
    //     handleChange({ target: { name: 'targetId', value } } as React.ChangeEvent<HTMLInputElement>, operation);
    //     if (value === '') {
    //         setIsManualInput(false);
    //     }
    // };

    /**
    * Generate the assert id based on the operation id
    * 
    * @param testStep
    * @param index
    * 
    */
    const generateAssertId = (operationId: string, testStep: string, index: number): string => {
        return `${operationId}-ASS-${String(index).padStart(index > 999 ? 4 : index > 99 ? 3 : 2, '0')}`;
    };

    /**
     * Delete an operation
     * 
     * @param operationId
     * 
     */
    const deleteOperation = (operationId: string) => {
        // Filter the operations to remove the deleted operation
        const updatedOperations = configs.operations.filter((operation: { id: string }) => operation.id !== operationId);
        // Recalculate the IDs of the remaining operations
        const newOperations = updatedOperations.map((operation: any, index: number) => ({
            ...operation,
            id: generateOperationId(configs.testStep, index + 1),
        }));
        // Recalculate asserts based on new operation IDs
        const updatedAsserts: any = {};
        newOperations.forEach((operation: any, index: number) => {
            const oldId = updatedOperations[index]?.id;
            if (configs.asserts[oldId]) {
                // Recalculate the asserts for the deleted operation
                updatedAsserts[operation.id] = configs.asserts[oldId].map((assert: any, assertIndex: number) => ({
                    ...assert,
                    // Generate a new id for the assert based on the new operation ID
                    id: generateAssertId(operation.id, configs.testStep, assertIndex + 1),
                }));
            }
        });
        // Update the operations form data
        configs.setOperationFormData((prevFormData: any) => ({
            ...prevFormData,
            [configs.testStep]: newOperations,
        }));
        // Update the asserts form data
        if (configs.setAssertFormData) {
            configs.setAssertFormData(updatedAsserts);
        }
    };

    /**
     * Handle the delete of a operation from the faTrash icon to display a confirmation message
     * 
     * @param operationId 
     */
    const handleDeleteOperation = (operationId: string) => {
        const confirmDelete = window.confirm(i18n.t('confirmationmessage.deleteoperation'));
        if (confirmDelete) {
            deleteOperation(operationId);
        }
    };

    /**
     * Get the type options based on the test step
     * 
     * @param testStep 
     * 
     */
    const getTypeOptions = (testStep: typeof configs.testStep) => {
        switch (testStep) {
            case 'setup':
            case 'test':
                return typeOptions;
            case 'teardown':
                return typeOptions.filter(option => [
                    'create',
                    'update',
                    'delete',
                    'transaction'
                ].includes(option.code));
        }
    };

    /**
     * Validate if the fields of the operation are filled before adding it in the accordion operation section
     * 
     * @param operation 
     * @returns 
     */
    const validateOperationFields = (operation: any) => {
        let combinedRequiredFields: string[] = [];
        if (operation.operationType === 'free-operation') {
            combinedRequiredFields = FreeOperationRequiredFields['free-operation'];
        } else {
            const defaultRequiredFields = requiredFieldsByType.default;
            const specificRequiredFields = requiredFieldsByType[operation.type?.code as keyof typeof requiredFieldsByType] || [];
            combinedRequiredFields = [...new Set([...defaultRequiredFields, ...specificRequiredFields])];
            if (configs.testStep === 'teardown') {
                const index = combinedRequiredFields.indexOf('operationType');
                if (index > -1) {
                    combinedRequiredFields.splice(index, 1);
                }
            }
        }
        if (operation.customCode && !/^[^\s]+$/.test(operation.customCode)) {
            setMessages(prevMessages => ({
                ...prevMessages,
                customCode: i18n.t('errormessage.nowhitespace'),
            }));
            return false;
        }
        if (operation.customCode && !/^[^\s]+$/.test(operation.customCode)) {
            setMessages(prevMessages => ({
                ...prevMessages,
                customCode: i18n.t('errormessage.nowhitespace'),
            }));
            return false;
        }
        if (operation.customSystem && !/^[^\s]+$/.test(operation.customSystem)) {
            setMessages(prevMessages => ({
                ...prevMessages,
                customSystem: i18n.t('errormessage.nowhitespace'),
            }));
            return false;
        }
        for (const field of combinedRequiredFields) {
            if (field === 'targetId' && operation.sourceId === '' &&
                (operation.url !== '' || operation.params !== '') &&
                !['capabilities', 'search', 'transaction', 'history'].includes(operation.type?.code)) {
                continue;
            }
            if (!operation[field]) {
                setMessages(prevMessages => ({
                    ...prevMessages,
                    [field]: i18n.t('errormessage.requiredfield')
                }));
                return false;
            }
        }
        const hasAtLeastOneField = operation.sourceId || operation.targetId || operation.params || operation.url;
        if (!hasAtLeastOneField && operation.type?.code !== 'capabilities') {
            setMessages(prevMessages => ({
                ...prevMessages,
                sourceId: i18n.t('errormessage.atleastonefield'),
                targetId: i18n.t('errormessage.atleastonefield'),
                params: i18n.t('errormessage.atleastonefield'),
                url: i18n.t('errormessage.atleastonefield'),
            }));
            return false;
        }
        setMessages(prevMessages => {
            const newMessages = { ...prevMessages };
            delete newMessages.sourceId;
            delete newMessages.targetId;
            delete newMessages.params;
            delete newMessages.url;
            return newMessages;
        });
        return true;
    };

    /**
     * Check if the field is required based on the type of operation
     * 
     * @param field 
     * @param operationType 
     *  
     */
    const isFieldRequired = (field: string, operationType?: string, operation?: any) => {
        if (field === 'targetId') {
            // Check if targetId is required based on the operation type and other conditions
            const requiredFields = requiredFieldsByType[operationType as keyof typeof requiredFieldsByType] || requiredFieldsByType.default;
            return requiredFields.includes('targetId') || (
                operation.sourceId === '' &&
                (operation.url !== '' || operation.params !== '') &&
                !['capabilities', 'search', 'transaction', 'history'].includes(operation.type?.code)
            );
        }
        const requiredFields = formData.operationType === 'free-operation'
            ? FreeOperationRequiredFields['free-operation']
            : requiredFieldsByType[operationType as keyof typeof requiredFieldsByType] || requiredFieldsByType.default;
        return requiredFields.includes(field);
    };

    /**
     * Display the error message when the field is required
     * 
     * @param field 
     * @returns 
     */
    const displayMessage = (field: string) => {
        return messages && messages[field] ? (
            <InputGroup className="position-absolute text-danger fs-08">
                {messages[field]}
            </InputGroup>
        ) : null;
    }

    /**
     * Check if the field is conditionally required based on the operation type
     * 
     * @param field 
     * 
     */
    const isFieldConditionallyRequired = (field: string): boolean => {
        const hasAtLeastOneField = formData.sourceId || formData.targetId || formData.params || formData.url;
        if (!hasAtLeastOneField) {
            return true;
        }
        return false;
    };

    /**
     * Render the field based on the type of operation
     * 
     * @param field 
     * @returns 
     */
    const renderField = (field: any, operation: any, isEditing: boolean) => {
        const fieldRequired = isFieldRequired(field, operation.type.code || operation.operationType, operation);
        // Fields
        switch (field) {
            case 'method':
                return (
                    <div className="md-flex-container position-relative">
                        <Form.Label className="fw-bold">
                            {i18n.t('label.method')} {isEditing ? (fieldRequired ? '*' : '') : ':'} &nbsp;
                        </Form.Label>
                        {isEditing ? (
                            <>
                                <Form.Select
                                    required={fieldRequired}
                                    name="method"
                                    value={operation.method}
                                    onChange={(e) => handleChange(e, operation)}
                                >
                                    {formData.method === '' && (
                                        <option
                                            className="text-muted"
                                            value=""
                                        >
                                            {i18n.t('placeholder.choosemethod')}
                                        </option>
                                    )}
                                    {customMethodOptions.map((option, index) => (
                                        option && (
                                            <option
                                                key={index}
                                                value={option.code}
                                            >
                                                {option.display}
                                            </option>
                                        )
                                    ))}
                                </Form.Select>
                                {displayMessage('method')}
                            </>
                        ) : (
                            operation.method !== '' && (
                                <Form.Text>{operation.method}</Form.Text>
                            )
                        )}
                    </div>
                );
            case 'customSystem':
                return (
                    <div className="md-flex-container">
                        <Form.Label className="fw-bold">
                            {i18n.t('label.system')} {isEditing ? (fieldRequired ? '*' : '') : ':'}&nbsp;
                        </Form.Label>
                        {isEditing ? (
                            <>
                                <InputGroup>
                                    <Form.Control
                                        required={fieldRequired}
                                        name="customSystem"
                                        value={operation.customSystem}
                                        onChange={(e) => handleCustomCodeAndSystemChange('customSystem', e.target.value, operation)}
                                        pattern="^[^\s]+$"
                                        isInvalid={!!messages.customSystem}
                                    />
                                </InputGroup>
                                {displayMessage('customSystem')}
                            </>
                        ) : (
                            operation.customSystem !== '' && (
                                <Form.Text>{operation.customSystem}</Form.Text>
                            )
                        )}
                    </div>
                );
            case 'customCode':
                return (
                    <div className="md-flex-container">
                        <Form.Label className="fw-bold">
                            Code {isEditing ? (fieldRequired ? '*' : '') : ':'}&nbsp;
                        </Form.Label>
                        {isEditing ? (
                            <>
                                <InputGroup>
                                    <Form.Control
                                        required={fieldRequired}
                                        name="customCode"
                                        value={operation.customCode}
                                        onChange={(e) => handleCustomCodeAndSystemChange('customCode', e.target.value, operation)}
                                        pattern="^[^\s]+$"
                                        isInvalid={!!messages.customCode}
                                    />
                                </InputGroup>
                                {displayMessage('customCode')}
                            </>
                        ) : (
                            operation.customCode !== '' && (
                                <Form.Text>{operation.customCode}</Form.Text>
                            )
                        )}
                    </div>
                );
            case 'resource':
                return (
                    <div className="md-flex-container">
                        <div className="d-flex">
                            <Form.Label className="fw-bold">
                                {i18n.t('label.resource')} {isEditing ? (fieldRequired ? '*' : '') : ':'}&nbsp;
                            </Form.Label>
                            {!isEditing && operation.resource !== '' && (
                                <div>
                                    <Form.Text>{operation.resource}</Form.Text>
                                    <FontAwesomeIcon
                                        icon={faCircleInfo}
                                        title={resourceTooltip}
                                        className="ms-2 actionIcon"
                                    />
                                </div>
                            )}
                            {isEditing && (
                                <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    title={resourceTooltip}
                                    className="mt-1 actionIcon"
                                />
                            )}
                        </div>
                        {isEditing && (
                            <>
                                <InputGroup>
                                    <Form.Control
                                        required={fieldRequired}
                                        name="resource"
                                        value={operation.resource}
                                        onChange={(e) => handleChange(e, operation)}
                                    />
                                </InputGroup>
                                {displayMessage('resource')}
                            </>
                        )}
                    </div>
                );
            case 'returnResourceType':
                return (
                    <div className="md-flex-container">
                        <Form.Label className="fw-bold">
                            {i18n.t('label.returnresourcetype')} {isEditing ? (fieldRequired ? '*' : '') : ':'}&nbsp;
                        </Form.Label>
                        {isEditing ? (
                            <>
                                <InputGroup>
                                    <Form.Control
                                        required={fieldRequired}
                                        name="returnResourceType"
                                        value={operation.returnResourceType}
                                        onChange={(e) => handleChange(e, operation)}
                                    />
                                </InputGroup>
                                {displayMessage('returnResourceType')}
                            </>
                        ) : (
                            operation.returnResourceType !== '' && (
                                <Form.Text>{operation.returnResourceType}</Form.Text>
                            )
                        )}
                    </div>
                );
            case 'accept':
                return (
                    <div className="md-flex-container">
                        <div className="d-flex">
                            <Form.Label className="fw-bold">
                                Accept {isEditing ? (fieldRequired ? '*' : '') : ':'}&nbsp;
                            </Form.Label>
                            {!isEditing && operation.accept !== '' && (
                                <div>
                                    <Form.Text>{operation.accept}</Form.Text>
                                    <FontAwesomeIcon
                                        icon={faCircleInfo}
                                        title={acceptTooltip}
                                        className="ms-2 actionIcon"
                                    />
                                </div>
                            )}
                            {isEditing && (
                                <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    title={acceptTooltip}
                                    className="mt-1 actionIcon"
                                />
                            )}
                        </div>
                        {isEditing && (
                            <InputGroup>
                                <Form.Control
                                    required={fieldRequired}
                                    name="accept"
                                    value={operation.accept || 'application/fhir+json'}
                                    onChange={(e) => handleChange(e, operation)}
                                />
                            </InputGroup>
                        )}
                    </div>
                );
            case 'contentType':
                return (
                    <div className="md-flex-container">
                        <div className="d-flex">
                            <Form.Label className="fw-bold">
                                {i18n.t('label.contenttype')} {isEditing ? '' : ':'}&nbsp;
                            </Form.Label>
                            {!isEditing && operation.contentType !== '' && (
                                <div>
                                    <Form.Text>{operation.contentType}</Form.Text>
                                    <FontAwesomeIcon
                                        icon={faCircleInfo}
                                        title={contentTypeTooltip}
                                        className="ms-2 actionIcon"
                                    />
                                </div>
                            )}
                            {isEditing && (
                                <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    title={contentTypeTooltip}
                                    className="mt-1 actionIcon"
                                />
                            )}
                        </div>
                        {isEditing && (
                            <InputGroup>
                                <Form.Control
                                    name="contentType"
                                    value={operation.contentType}
                                    onChange={(e) => handleChange(e, operation)}
                                />
                            </InputGroup>
                        )}
                    </div>
                );
            case 'origin':
                return (
                    <div className="md-flex-container">
                        <Form.Label className="fw-bold">
                            {i18n.t('general.origin')} {isEditing ? '' : ':'}&nbsp;
                        </Form.Label>
                        {isEditing ? (
                            <Form.Select
                                name="origin"
                                value={configs.origins[Number(operation.origin) - 1]?.profile || ''}
                                onChange={(e) => handleChange(e, operation)}
                            >
                                <option
                                    className="text-muted"
                                    value=""
                                >
                                    {i18n.t('placeholder.origin')}
                                </option>
                                {configs.origins.map((origin, index) => (
                                    <option
                                        key={index}
                                        value={origin.profile}
                                    >
                                        {index + 1} | {origin.profile}
                                    </option>
                                ))}
                            </Form.Select>
                        ) : (
                            operation.origin !== '' && (
                                <Form.Text>
                                    {(() => {
                                        const originIndex = Number(operation.origin) - 1;
                                        const origin = configs.origins[originIndex];
                                        return origin ? `${originIndex + 1} | ${origin.profile}` : operation.origin;
                                    })()}
                                </Form.Text>
                            )
                        )}
                    </div>
                );
            case 'destination':
                return (
                    <div className="md-flex-container">
                        <Form.Label className="fw-bold">
                            Destination {isEditing ? '' : ':'}&nbsp;
                        </Form.Label>
                        {isEditing ? (
                            <Form.Select
                                name="destination"
                                value={configs.destinations[Number(operation.destination) - 1]?.profile || ''}
                                onChange={(e) => handleChange(e, operation)}
                            >
                                <option
                                    className="text-muted"
                                    value=""
                                >
                                    {i18n.t('placeholder.destination')}
                                </option>
                                {configs.destinations.map((destination, index) => (
                                    <option
                                        key={index}
                                        value={destination.profile}
                                    >
                                        {index + 1} | {destination.profile}
                                    </option>
                                ))}
                            </Form.Select>
                        ) : (
                            operation.destination !== '' && (
                                <Form.Text>
                                    {(() => {
                                        const destinationIndex = Number(operation.destination) - 1;
                                        const destination = configs.destinations[destinationIndex];
                                        return destination ? `${destinationIndex + 1} | ${destination.profile}` : operation.destination;
                                    })()}
                                </Form.Text>
                            )
                        )}
                    </div>
                );
            case 'sourceId':
                return (
                    <div className="md-flex-container">
                        <div className="d-flex">
                            <Form.Label className="fw-bold">
                                {i18n.t('label.sourceid')} {isEditing ? (fieldRequired ? '*' : '') : ':'}&nbsp;
                            </Form.Label>
                            {!isEditing && operation.sourceId !== '' && (
                                <div>
                                    <Form.Text>
                                        {configs.fixtures.find(fixture => fixture.internalId === operation.sourceId)?.id ?? operation.sourceId}
                                    </Form.Text>
                                    <FontAwesomeIcon
                                        icon={faCircleInfo}
                                        title={sourceIdTooltip}
                                        className="ms-2 actionIcon"
                                    />
                                </div>
                            )}
                            {isEditing && (
                                <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    title={sourceIdTooltip}
                                    className="mt-1 actionIcon"
                                />
                            )}
                        </div>
                        {isEditing && (
                            <>
                                <Form.Select
                                    required={fieldRequired || isFieldConditionallyRequired('sourceId')}
                                    name="sourceId"
                                    value={operation.sourceId}
                                    onChange={(e) => handleChange(e, operation)}
                                >
                                    <option className="text-muted" value="">
                                        {i18n.t('placeholder.choosefixture')}
                                    </option>
                                    {configs.fixtures.map(fixture => (
                                        <option
                                            key={fixture.internalId}
                                            value={fixture.internalId}
                                        >
                                            {fixture.id}
                                        </option>
                                    ))}
                                </Form.Select>
                                {displayMessage('sourceId')}
                            </>
                        )}
                    </div>
                );
            case 'targetId':
                return (
                    <div className="md-flex-container">
                        <div className="d-flex">
                            <Form.Label className="fw-bold">
                                {i18n.t('label.targetid')} {isEditing ? (fieldRequired ? '*' : '') : ':'}&nbsp;
                            </Form.Label>
                            {!isEditing && operation.targetId !== '' && (
                                <div>
                                    <Form.Text>
                                        {configs.fixtures.find(fixture => fixture.internalId === operation.targetId)?.id ??
                                            configs.variables.find(variable => variable.internalId === operation.targetId)?.name ??
                                            operation.targetId}
                                    </Form.Text>
                                    <FontAwesomeIcon
                                        icon={faCircleInfo}
                                        title={targetIdTooltip}
                                        className="ms-2 actionIcon"
                                    />
                                </div>
                            )}
                            {isEditing && (
                                <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    title={targetIdTooltip}
                                    className="mt-1 actionIcon"
                                />
                            )}
                        </div>
                        {isEditing && (
                            <>
                                <Form.Select
                                    required={fieldRequired || isFieldConditionallyRequired('targetId')}
                                    name="targetId"
                                    value={operation.targetId}
                                    onChange={(e) => handleSelectChange(e, operation)}
                                    disabled={
                                        operation.sourceId === '' &&
                                        (operation.url !== '' || operation.params !== '') &&
                                        !['capabilities', 'search', 'transaction', 'history'].includes(operation.type?.code)
                                    }
                                >
                                    <option className="text-muted" value="">
                                        {i18n.t('placeholder.choosefixtureorvariable')}
                                    </option>
                                    {combinedTargetIdOptions.map((combinedOption, index) => (
                                        <option
                                            key={index}
                                            value={combinedOption.id}
                                        >
                                            {combinedOption.name}
                                        </option>
                                    ))}
                                    {/* <option value="manual">
                                    {i18n.t('label.entermanually')}
                                </option> */}
                                </Form.Select>
                                {displayMessage('targetId')}
                            </>
                        )}
                    </div>
                );
            case 'responseId':
                return (
                    <div className="md-flex-container">
                        <div className="d-flex">
                            <Form.Label className="fw-bold">
                                {i18n.t('label.responseId')} {isEditing ? '' : ':'}&nbsp;
                            </Form.Label>
                            {!isEditing && operation.responseId !== '' && (
                                <div>
                                    <Form.Text>
                                        {configs.fixtures.find(fixture => fixture.internalId === operation.responseId)?.id ?? operation.responseId}
                                    </Form.Text>
                                    <FontAwesomeIcon
                                        icon={faCircleInfo}
                                        title={responseIdTooltip}
                                        className="ms-2 actionIcon"
                                    />
                                </div>
                            )}
                            {isEditing && (
                                <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    title={responseIdTooltip}
                                    className="mt-1 actionIcon"
                                />
                            )}
                        </div>
                        {isEditing && (
                            <Form.Select
                                name="responseId"
                                value={operation.responseId}
                                onChange={(e) => handleChange(e, operation)}
                            >
                                <option className="text-muted" value="">
                                    {i18n.t('placeholder.choosefixture')}
                                </option>
                                {configs.fixtures.map(fixture => (
                                    <option
                                        key={fixture.internalId}
                                        value={fixture.internalId}
                                    >
                                        {fixture.id}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                    </div>
                );
            case 'url':
                return (
                    <div className="md-flex-container">
                        <Form.Label className="fw-bold">
                            URL {isEditing ? (fieldRequired ? '*' : '') : ':'}&nbsp;
                        </Form.Label>
                        {isEditing ? (
                            <>
                                <InputGroup>
                                    <Form.Control
                                        required={isFieldConditionallyRequired('url')}
                                        name="url"
                                        value={operation.url}
                                        onChange={(e) => handleChange(e, operation)}
                                        disabled={
                                            operation.sourceId === '' &&
                                            (operation.targetId !== '' || operation.params !== '') &&
                                            !['capabilities', 'search', 'transaction', 'history',].includes(operation.type?.code)}
                                    />
                                </InputGroup>
                                {displayMessage('url')}
                            </>
                        ) : (
                            operation.url !== '' && (
                                <Form.Text>{operation.url}</Form.Text>
                            )
                        )}
                    </div>
                );
            case 'params':
                return (
                    <div className="md-flex-container">
                        <div className="d-flex">
                            <Form.Label className="fw-bold">
                                Params {isEditing ? '' : ':'}&nbsp;
                            </Form.Label>
                            {!isEditing && operation.params !== '' && (
                                <div>
                                    <Form.Text>{operation.params}</Form.Text>
                                    <FontAwesomeIcon
                                        icon={faCircleInfo}
                                        title={paramsTooltip}
                                        className="ms-2 actionIcon"
                                    />
                                </div>
                            )}
                            {isEditing && (
                                <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    title={paramsTooltip}
                                    className="mt-1 actionIcon"
                                />
                            )}
                        </div>
                        {isEditing && (
                            <>
                                <InputGroup>
                                    <Form.Control
                                        required={isFieldConditionallyRequired('params')}
                                        name="params"
                                        value={operation.params}
                                        onChange={(e) => handleChange(e, operation)}
                                        disabled={
                                            operation.sourceId === '' &&
                                            (operation.targetId !== '' || operation.url !== '') &&
                                            !['capabilities', 'search', 'transaction', 'history'].includes(operation.type?.code)
                                        }
                                    />
                                </InputGroup>
                                {displayMessage('params')}
                            </>
                        )}
                    </div>
                );
            case 'encodeRequestUrl':
                return (
                    <div className="md-flex-container d-flex gap-2">
                        {isEditing ? (
                            <Form.Check
                                name="encodeRequestUrl"
                                className="fw-bold"
                                type="switch"
                                label={i18n.t('label.encoderequesturl')}
                                defaultChecked={false}
                                checked={operation.encodeRequestUrl}
                                onChange={(e) => handleSwitchChange(e)}
                            />
                        ) : (
                            <Form.Check
                                name="encodeRequestUrl"
                                className="fw-bold"
                                type="switch"
                                label={i18n.t('label.encoderequesturl')}
                                defaultChecked={false}
                                checked={operation.encodeRequestUrl}
                                onChange={(e) => handleSwitchChange(e)}
                            />
                        )}
                        <FontAwesomeIcon
                            icon={faCircleInfo}
                            title={encodeRequestUrlTooltip}
                            className="mt-1 actionIcon"
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    /**
     * Handle the change of the switch input field
     * 
     * @param e 
     * 
     */
    const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        setFormData(prevFormData => ({
            ...prevFormData,
            [name]: checked
        }));
    };

    /**
     * Reinitialize the form data when an operation is added
     * 
     */
    const reinitializeFormData = () => {
        setEditionValidated(false);
        setFormData({
            id: '',
            operationType: '',
            type: {
                system: '',
                code: ''
            },
            customSystem: '',
            customCode: '',
            label: '',
            resource: '',
            method: '',
            returnResourceType: '',
            description: '',
            accept: '',
            sourceId: '',
            origin: '',
            responseId: '',
            contentType: '',
            targetId: '',
            destination: '',
            url: '',
            params: '',
            encodeRequestUrl: false,
            isCustomOperation: false
        });
    };

    /**
     * Add a new operation to the list of operations
     * 
     */
    const submitOperationForm = () => {
        let newOperation = {
            ...formData,
            id: currentOperationId !== null ? currentOperationId : generateOperationId(configs.testStep, configs.operations.length + 1),
            operationType: formData.operationType,
            type: {
                system: formData.type.system || (formData.type.code === 'custom-operation' ? formData.customSystem : "http://hl7.org/fhir/restful-interaction"),
                code: formData.type.code === 'custom-operation' ? formData.customCode : formData.type.code
            },
            isCustomOperation: formData.isCustomOperation,
        };
        if (formData.operationType === 'free-operation') {
            newOperation = {
                ...newOperation,
                accept: '',
                method: '',
                type: { system: '', code: '' },
                customSystem: '',
                customCode: '',
            };
        }
        if (!validateOperationFields(newOperation)) {
            setEditionValidated(true);
            return;
        }
        let updatedOperations;
        if (currentOperationId !== null) {
            // Update existing operation
            updatedOperations = configs.operations.map((operation: any) =>
                operation.id === currentOperationId ? newOperation : operation
            );
        } else {
            // Add new operation
            updatedOperations = [...configs.operations, newOperation];
        }
        configs.setOperationFormData((prevFormData: any) => ({
            ...prevFormData,
            [configs.testStep]: updatedOperations,
        }));
        setMessages({});
        reinitializeFormData();
        setShowOperationForm(false);
        setCurrentOperationId(null);
    };

    /**
     * Cancel the operation form
     * 
     */
    const cancelOperationForm = () => {
        reinitializeFormData();
        setShowOperationForm(false);
        setDisplayTestSection(false);
        setCurrentOperationId(null);
    }

    /**
     * Handle the cancel of an operation and display a confirmation message
     * 
     */
    const handleCancelOperation = () => {
        if (window.confirm(i18n.t('confirmationmessage.canceloperation'))) {
            cancelOperationForm();
        }
    };

    /////////////////////////////
    //         Effects         //
    /////////////////////////////

    /**
     * Generate a new operation id when the test step changes
     * 
     */
    useEffect(() => {
        const newOperationId = generateOperationId(configs.testStep, configs.operations.length + 1);
        setFormData(prevFormData => ({
            ...prevFormData,
            id: newOperationId,
            type: {
                ...prevFormData.type,
                code: prevFormData.method
            }
        }));
    }, [configs.testStep]);

    /////////////////////////////
    //         Content         //
    /////////////////////////////

    // Get the type options based on the test step
    const testStepTypeOptions = getTypeOptions(configs.testStep);

    return (
        <>
            <Accordion defaultActiveKey="0" alwaysOpen>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>
                        <Title
                            level={2}
                            content={
                                configs.testStep === 'setup' ? 'Setup' :
                                    configs.testStep === 'teardown' ? 'Teardown' :
                                        'Test'
                            }
                        />
                    </Accordion.Header>
                    <Accordion.Body>
                        {!displayTestSection && (!configs.operations || configs.operations.length === 0) ? (
                            <>
                                <StatusAlert status={FhirStatus.information}>
                                    <div className='d-flex align-items-center gap-2'>
                                        <StatusIcon status={FhirStatus.information} />
                                        <strong>
                                            {configs.testStep === 'setup' ? i18n.t('infomessage.nosetup') :
                                                configs.testStep === 'teardown' ? i18n.t('infomessage.noteardown') :
                                                    i18n.t('infomessage.notest')}
                                        </strong>
                                    </div>
                                </StatusAlert>
                                <span
                                    className="actionIcon d-flex flex-wrap align-items-center gap-2 w-15rem"
                                    onClick={() => {
                                        setDisplayTestSection(true);
                                        setShowOperationForm(true);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                    <strong>
                                        {configs.testStep === 'setup' ? i18n.t('button.addsetup') :
                                            configs.testStep === 'teardown' ? i18n.t('button.addteardown') :
                                                i18n.t('button.addtest')}
                                    </strong>
                                </span>
                            </>
                        ) : (
                            <>
                                {/* ACCORDION SECTION WHEN AN OPERATION WAS ADDED */}
                                {configs.operations.length > 0 && configs.operations.map((operation: any, index: any) => {
                                    return (
                                        operation.id && currentOperationId !== operation.id &&
                                        // Display the operation form after the addition of the operation
                                        <Accordion
                                            key={index}
                                            className="mb-4"
                                        >
                                            <Accordion.Item eventKey="0" >
                                                <Accordion.Header>
                                                    <div className="d-flex w-100 justify-content-between me-3">
                                                        <Title
                                                            level={3}
                                                            content={operation.id}
                                                        >
                                                        </Title>
                                                        <div className="d-flex flex-wrap mt-3 gap-2">
                                                            <FontAwesomeIcon
                                                                icon={faPen}
                                                                className="actionIcon"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleOperationEditingClick(e, operation);
                                                                }}
                                                            />
                                                            <FontAwesomeIcon
                                                                icon={faTrash}
                                                                className="actionIcon"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteOperation(operation.id);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </Accordion.Header>
                                                <Accordion.Body>
                                                    <div className="d-flex flex-column gap-4">
                                                        <div className="d-flex flex-wrap w-100 gap-4">
                                                            <div className="md-flex-container">
                                                                <Form.Label className="fw-bold">
                                                                    Description :&nbsp;
                                                                </Form.Label>
                                                                <Form.Text>{operation.description}</Form.Text>
                                                            </div>
                                                            {configs.testStep !== 'teardown' && (
                                                                <div className="md-flex-container">
                                                                    <Form.Label className="fw-bold">
                                                                        {i18n.t('label.operationtype')} :&nbsp;
                                                                    </Form.Label>
                                                                    <Form.Text>{operation.operationType}</Form.Text>
                                                                </div>
                                                            )}
                                                            {
                                                                (operation.operationType && operation.operationType !== 'free-operation')
                                                                    || configs.testStep === 'teardown' ? (
                                                                    <>
                                                                        <div className="md-flex-container">
                                                                            <Form.Label className="fw-bold">
                                                                                Type :&nbsp;
                                                                            </Form.Label>
                                                                            <Form.Text>
                                                                                {operation.isCustomOperation ? 'custom-operation' : operation.type.code}
                                                                            </Form.Text>
                                                                        </div>
                                                                        <>
                                                                            {typeFields[operation.type.code as keyof typeof typeFields]?.map((field) => (
                                                                                (operation as any)[field] || field === 'encodeRequestUrl' ? renderField(field, operation, false) : null
                                                                            ))}
                                                                            {operation.isCustomOperation && operation.type.code !== 'custom-operation' && (
                                                                                typeFields['custom-operation']?.map((field) => (
                                                                                    (operation as any)[field] && (
                                                                                        renderField(field, operation, false)
                                                                                    )
                                                                                ))
                                                                            )}
                                                                        </>
                                                                    </>
                                                                ) : (
                                                                    operationTypeFields[operation.operationType as keyof typeof operationTypeFields]?.map((field) => (
                                                                        (operation as any)[field] || field === 'encodeRequestUrl' ? renderField(field, operation, false) : null
                                                                    ))
                                                                )
                                                            }
                                                        </div>
                                                        {!configs.testStep.includes('teardown') && (
                                                            <AddTestScriptAssert
                                                                testStep={configs.testStep}
                                                                asserts={configs.asserts[operation.id]}
                                                                setAssertFormData={configs.setAssertFormData || (() => { })}
                                                                selectedProfiles={configs.selectedProfiles || []}
                                                                fixtures={configs.fixtures}
                                                                operationId={operation.id}
                                                                operationType={operationType}
                                                                setShowOperationForm={setShowOperationForm}
                                                                showOperationForm={showOperationForm}
                                                            />
                                                        )}
                                                    </div>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion>
                                    );
                                })}

                                {/* SECTION TO ADD OR EDIT AN OPERATION */}
                                {
                                    (showOperationForm ? (
                                        <div className={`d-flex flex-wrap gap-4 ${editionValidated ? 'was-validated' : ''}`}>
                                            <div className="d-flex flex-wrap w-100 gap-4">
                                                <div className="w-100">
                                                    <Form.Label className="fw-bold">
                                                        Description *
                                                    </Form.Label>
                                                    <Form.Control
                                                        required
                                                        name="description"
                                                        as="textarea"
                                                        rows={5}
                                                        value={formData.description}
                                                        onChange={handleChange}
                                                        className="form-control"
                                                    />
                                                    {displayMessage('description')}
                                                </div>
                                                {configs.testStep !== 'teardown' && (
                                                    <div className="w-100">
                                                        <Form.Label className="fw-bold">
                                                            {i18n.t('label.operationtype')} *
                                                        </Form.Label>
                                                        <Form.Select
                                                            required
                                                            name="operationType"
                                                            value={formData.operationType}
                                                            onChange={handleChange}
                                                        >
                                                            <option
                                                                className='text-muted'
                                                                value=""
                                                            >
                                                                {i18n.t('placeholder.chooseoperationtype')}
                                                            </option>
                                                            {operationTypeOptions.map((option, index) => (
                                                                <option
                                                                    key={index}
                                                                    value={option.code}
                                                                >
                                                                    {option.display}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </div>
                                                )}
                                                {formData.operationType === '' && configs.testStep !== 'teardown' && (
                                                    <Button
                                                        className="w-9rem"
                                                        role="button"
                                                        variant="secondary"
                                                        onClick={handleCancelOperation}
                                                    >
                                                        {i18n.t('button.cancel')}
                                                    </Button>
                                                )}
                                                {
                                                    (formData.operationType && formData.operationType !== 'free-operation')
                                                        || configs.testStep === 'teardown' ? (
                                                        <>
                                                            <div className="w-100">
                                                                <Form.Label className="fw-bold">
                                                                    Type *
                                                                </Form.Label>
                                                                <Form.Select
                                                                    required
                                                                    name="type"
                                                                    value={formData.isCustomOperation ? 'custom-operation' : formData.type.code}
                                                                    onChange={(e) => handleChange(e, formData)}
                                                                >
                                                                    <option
                                                                        className="text-muted"
                                                                        value=""
                                                                    >
                                                                        {i18n.t('placeholder.choosetype')}
                                                                    </option>
                                                                    {(testStepTypeOptions ?? []).map((option, index) => (
                                                                        <option
                                                                            key={index}
                                                                            value={option?.code}
                                                                        >
                                                                            {option.display}
                                                                        </option>
                                                                    ))}
                                                                </Form.Select>
                                                            </div>
                                                            {formData.type.code === '' && (
                                                                <Button
                                                                    className="w-9rem"
                                                                    role="button"
                                                                    variant="secondary"
                                                                    onClick={handleCancelOperation}
                                                                >
                                                                    {i18n.t('button.cancel')}
                                                                </Button>
                                                            )}
                                                            <>
                                                                {(formData.isCustomOperation || formData.type.code === 'custom-operation') ? (
                                                                    typeFields['custom-operation']?.map((field) => (
                                                                        renderField(field, formData, true)
                                                                    ))
                                                                ) : (
                                                                    typeFields[formData.type.code as keyof typeof typeFields]?.map((field) => (
                                                                        renderField(field, formData, true)
                                                                    ))
                                                                )}
                                                            </>
                                                        </>
                                                    ) : (
                                                        operationTypeFields[formData.operationType as keyof typeof operationTypeFields]?.map((field) => (
                                                            renderField(field, formData, true)
                                                        ))
                                                    )
                                                }
                                            </div>
                                            {(formData.type.code !== '' || formData.operationType === 'free-operation') && (
                                                <div className="d-flex gap-4">
                                                    <Button
                                                        className="w-9rem"
                                                        type="button"
                                                        variant="primary"
                                                        onClick={submitOperationForm}
                                                    >
                                                        {i18n.t('button.add')}
                                                    </Button>
                                                    <Button
                                                        className="w-9rem"
                                                        role="button"
                                                        variant="secondary"
                                                        onClick={handleCancelOperation}
                                                    >
                                                        {i18n.t('button.cancel')}
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div
                                            className="actionIcon d-flex flex-wrap align-items-center gap-2 w-15rem"
                                            onClick={() => { setShowOperationForm(true) }}
                                        >
                                            <FontAwesomeIcon
                                                icon={faPlus}
                                                role="button"
                                            />
                                            <strong
                                                role="button"
                                            >
                                                {i18n.t('general.addoperation')}
                                            </strong>
                                        </div>
                                    ))}
                            </>
                        )}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </>
    );
};

export default OperationsSection;